import React from "react";
import { Redirect } from "react-router-dom";
import { Modal } from "react-bootstrap";
import Context from "../../../Context";
import TemplateEditor from "../../Editor/TemplateEditor";
import BigError from "../../Misc/BigError";
import Blocker from "../../Misc/Blocker";
import TemplateTitle from "./TemplateTitle";

const DRAFT_PREFIX = "ship-safety.manual.draft.";

export default class TemplateEdit extends React.Component {
  static contextType = Context;
  constructor(props) {
    super(props);
    this.state = {
      redirect: false,
      pending: false,
      doc: false,
      showEditor: false,
      currentTitle: "Новый",
      currentLink: "",
      documentHash: null,
      documentLastUpdate: null,
      editorKey: 0,
      conflict: null,
    };
  }
  componentDidMount() {
    this.load();
  }

  getDraftKey = () => {
    if (this.props.id) return `${DRAFT_PREFIX}edit.${this.props.id}`;
    if (this.props.copyObjId) return `${DRAFT_PREFIX}copy.${this.props.copyObjId}`;
    return `${DRAFT_PREFIX}new.${this.props.structure}.${this.props.document}`;
  };

  readDraft = () => {
    try {
      const value = window.localStorage.getItem(this.getDraftKey());
      const draft = value ? JSON.parse(value) : null;
      return draft && draft.content && Array.isArray(draft.content.blocks)
        ? draft
        : null;
    } catch (error) {
      return null;
    }
  };

  writeDraft = (content, html, label) => {
    try {
      window.localStorage.setItem(
        this.getDraftKey(),
        JSON.stringify({
          version: 1,
          content,
          rendered: html || null,
          label,
          savedAt: new Date().toISOString(),
        })
      );
      return true;
    } catch (error) {
      return false;
    }
  };

  removeDraft = () => {
    try {
      window.localStorage.removeItem(this.getDraftKey());
    } catch (error) {
      // A temporary draft is optional when browser storage is unavailable.
    }
  };

  restoreDraft = (doc, title) => {
    const draft = this.readDraft();
    if (!draft) return { doc: doc || {}, title, restored: false };

    return {
      doc: Object.assign({}, doc || {}, {
        content: JSON.stringify(draft.content),
        rendered: draft.rendered || (doc && doc.rendered),
      }),
      title: draft.label || title,
      restored: true,
    };
  };

  getDocumentContent = () => {
    const { doc } = this.state;
    if (doc && doc.content) {
      try {
        const content = JSON.parse(doc.content);
        if (Array.isArray(content.blocks)) return content;
      } catch (error) {
        // Fall through to a valid empty Draft.js document.
      }
    }
    return {
      blocks: [
        {
          key: "autosave",
          text: "",
          type: "unstyled",
          depth: 0,
          inlineStyleRanges: [],
          entityRanges: [],
          data: {},
        },
      ],
      entityMap: {},
    };
  };

  load = async () => {
    if (this.props.id) {
      this.setState({ pending: true });
      let doc = await this.context.api.q("/manual/get", {
        id: this.props.id,
      });
      this.setState({ doc });
      if (doc.id && String(doc.structure) === this.props.structure) {
        const restored = this.restoreDraft(doc, doc.label || "");
        this.setState({
          doc: restored.doc,
          showEditor: true,
          currentTitle: restored.title,
          currentLink: "",
          documentHash: doc.hash || null,
          documentLastUpdate: doc.lastupdate || null,
          autoSaveRestored: restored.restored,
          autoSaveAvailable: restored.restored,
        }, this.showTitle);
      } else {
        this.setState({ notFound: true });
      }
      this.setState({ pending: false });
    } else {
      if (this.props.copyObjId) {
        this.setState({ pending: true });
        let doc = await this.context.api.q("/manual/get", {
          id: this.props.copyObjId,
        });
        if (doc.id) {
          doc.id = null;
          const restored = this.restoreDraft(doc, doc.label || "");
          this.setState({
            doc: restored.doc,
            showEditor: true,
            currentTitle: restored.title,
            currentLink: "",
            autoSaveRestored: restored.restored,
            autoSaveAvailable: restored.restored,
          }, this.showTitle);
        } else {
          this.setState({ notFound: true });
        }
        this.setState({ pending: false });
      } else {
        const restored = this.restoreDraft(null, "Новый");
        this.setState({
          doc: restored.doc,
          showEditor: true,
          currentTitle: restored.title,
          currentLink: "",
          autoSaveRestored: restored.restored,
          autoSaveAvailable: restored.restored,
        }, this.showTitle);
      }
    }
  };

  onTitleChange = (newTitle) => {
    this.setState({ currentTitle: newTitle }, () => {
      const draft = this.readDraft();
      const saved = this.writeDraft(
        draft ? draft.content : this.getDocumentContent(),
        draft ? draft.rendered : this.state.doc && this.state.doc.rendered,
        newTitle
      );
      if (saved) this.setState({ autoSaveAvailable: true });
    });
  };

  showTitle = () => {
    const { currentTitle, doc } = this.state;

    this.context.titleAreaSet(
      <TemplateTitle
        editable={true}
        title={currentTitle}
        onTitleChange={this.onTitleChange}
      />
    );
    this.context.browserTitleSet(currentTitle);
  };

  onSave = async (editorState, html) => {
    let structure = this.props.structure;
    if (!structure || structure === "all") {
      this.context.toast("Необходимо выбрать раздел документации", "error");
      return false;
    }

    let elem = document.createElement("div");
    elem.innerHTML = html;
    let text = elem.innerText;

    let type = this.props.id
      ? this.state.doc.type
      : this.context.getType(`public.${this.props.document}`);

    const payload = {
      id: this.props.id || null,
      content: editorState,
      rendered: html || null,
      data: text,
      label: this.state.currentTitle,
      structure: structure,
      type,
    };
    let result = await this.context.api.q(
      this.props.id ? "/manual/set_checked" : "/manual/set",
      this.props.id
        ? Object.assign({}, payload, {
            expectedhash: this.state.documentHash,
            expectedlastupdate: this.state.documentLastUpdate,
          })
        : payload
    );

    if (result && result.conflict) {
      this.setState({
        conflict: {
          content: editorState,
          rendered: html || null,
          hash: result.hash || null,
        },
      });
      return false;
    }
    if (result !== false) {
      this.removeDraft();
      this.setState({
        autoSaveAvailable: false,
        documentHash: result.hash || this.state.documentHash,
        documentLastUpdate: result.lastupdate || this.state.documentLastUpdate,
      });
      if (this.props.add && result.id) {
        this.setState({
          redirect: `/manual/${this.props.structure}/${result.id}/edit`,
        });
      }
      return true;
    } else {
      this.context.toast("Не удалось сохранить изменения", "error");
      return false;
    }
  };
  onAutoSave = (editorState, html) => {
    const saved = this.writeDraft(editorState, html, this.state.currentTitle);
    if (saved) this.setState({ autoSaveAvailable: true });
    return saved;
  };
  onDiscardAutoSave = () => {
    this.removeDraft();
    this.setState({ autoSaveAvailable: false });
    return true;
  };
  loadCurrentServerVersion = async () => {
    this.setState({ pending: true });
    const doc = await this.context.api.q("/manual/get", { id: this.props.id });
    if (doc && doc.id) {
      this.removeDraft();
      this.setState(
        (state) => ({
          doc,
          currentTitle: doc.label || "",
          documentHash: doc.hash || null,
          documentLastUpdate: doc.lastupdate || null,
          autoSaveAvailable: false,
          autoSaveRestored: false,
          conflict: null,
          editorKey: state.editorKey + 1,
        }),
        this.showTitle
      );
    } else {
      this.context.toast("Не удалось загрузить новую версию документа", "error");
    }
    this.setState({ pending: false });
  };
  saveConflictAsCopy = async () => {
    const { conflict } = this.state;
    if (!conflict) return;

    const elem = document.createElement("div");
    elem.innerHTML = conflict.rendered;
    const result = await this.context.api.q("/manual/set", {
      id: null,
      content: conflict.content,
      rendered: conflict.rendered,
      data: elem.innerText,
      label: `${this.state.currentTitle} (копия)`,
      structure: this.props.structure,
      type: this.state.doc.type,
    });

    if (result && result.id) {
      this.removeDraft();
      this.setState({
        conflict: null,
        autoSaveAvailable: false,
        redirect: `/manual/${this.props.structure}/${result.id}/edit`,
      });
    } else {
      this.context.toast("Не удалось сохранить отдельную копию", "error");
    }
  };
  onOpen = () => {
    let structure = this.props.structure;
    this.setState({ redirect: `/manual/${structure}/` });
  };
  onNew = () => {
    let structure = this.props.structure;

    let typecode = this.state.doc.typecode;
    if (typecode) {
      typecode = typecode.split(".").pop();
      if (!this.props.add) {
        this.setState({
          redirect: `/manual/${structure}/${typecode}/add/`,
        });
      }
    }
  };

  renderConflictDialog = () => {
    const { conflict } = this.state;
    return (
      <Modal
        show={Boolean(conflict)}
        onHide={() => this.setState({ conflict: null })}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Документ изменён другим пользователем</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          В документе появилась более новая версия. Ваши изменения не были
          перезаписаны. Загрузите новую версию или сохраните текущую работу
          отдельной копией.
        </Modal.Body>
        <Modal.Footer>
          <button
            type="button"
            className="btn btn-light"
            onClick={() => this.setState({ conflict: null })}
          >
            Продолжить редактирование
          </button>
          <button
            type="button"
            className="btn btn-light-primary"
            onClick={this.loadCurrentServerVersion}
          >
            Загрузить новую версию
          </button>
          <button
            type="button"
            className="btn btn-primary"
            onClick={this.saveConflictAsCopy}
          >
            Сохранить отдельной копией
          </button>
        </Modal.Footer>
      </Modal>
    );
  };

  render() {
    if (this.state.redirect) {
      return <Redirect to={this.state.redirect} />;
    }
    if (this.state.notFound) {
      return (
        <BigError
          title={"Документ не найден"}
          message={`Не удалось получить информацию о документе #${this.props.id}`}
        />
      );
    }
    return (
      <>
        <Blocker on={this.state.pending}>
          {this.state.showEditor ? (
            <TemplateEditor
              key={`${this.props.id || "new"}-${this.state.editorKey}`}
              add={this.props.add}
              content={this.state.doc.content}
              onOpen={this.onOpen}
              onNew={this.onNew}
              onSave={this.onSave}
              onAutoSave={this.onAutoSave}
              autoSaveRestored={this.state.autoSaveRestored}
              autoSaveAvailable={this.state.autoSaveAvailable}
              onDiscardAutoSave={this.onDiscardAutoSave}
            />
          ) : null}
        </Blocker>
        {this.renderConflictDialog()}
      </>
    );
  }
}
